import { useState } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';

const formatMoney = (v) => '$' + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BalanceTable({ title, balance, color, counterpartyLabel }) {
  const [expanded, setExpanded] = useState(false);
  if (!balance) return null;

  const hasCounterparties = balance.counterparties && balance.counterparties.length > 0;
  const mismatchCount = hasCounterparties ? balance.counterparties.filter(c => !c.match).length : 0;
  const displayList = expanded ? balance.counterparties : (balance.counterparties || []).slice(0, 10);

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
        {title}
        {balance.match
          ? <span className="text-emerald-400 text-[10px] font-normal flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Verified</span>
          : <span className="text-amber-400 text-[10px] font-normal flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {mismatchCount} mismatch{mismatchCount !== 1 ? 'es' : ''}</span>
        }
      </h3>
      <div className="bg-gray-800/30 border border-gray-800 rounded-xl p-4">
        {/* Totals */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-500">As of {balance.as_of_date}</span>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] text-gray-600">QB</div>
              <span className={`text-lg font-semibold font-mono ${color}`}>{formatMoney(balance.qb_total)}</span>
            </div>
            <div className="text-gray-700">|</div>
            <div className="text-right">
              <div className="text-[10px] text-gray-600">FinBoard</div>
              <span className={`text-lg font-semibold font-mono ${balance.match ? 'text-emerald-400' : 'text-amber-400'}`}>
                {formatMoney(balance.our_total)}
              </span>
            </div>
          </div>
        </div>

        {/* Per-counterparty table */}
        {hasCounterparties && (
          <div className="bg-gray-800/20 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-3 py-1.5 text-left text-[10px] text-gray-500 uppercase">{counterpartyLabel}</th>
                  <th className="px-3 py-1.5 text-right text-[10px] text-gray-500 uppercase">QB</th>
                  <th className="px-3 py-1.5 text-right text-[10px] text-gray-500 uppercase">FinBoard</th>
                  <th className="px-3 py-1.5 text-right text-[10px] text-gray-500 uppercase">Variance</th>
                </tr>
              </thead>
              <tbody>
                {displayList.map(c => (
                  <tr key={c.name} className="border-b border-gray-800/30">
                    <td className="px-3 py-1.5 text-gray-300 truncate max-w-[200px]">{c.name}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{formatMoney(c.qb_balance)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{formatMoney(c.our_balance)}</td>
                    <td className={`px-3 py-1.5 text-right font-mono ${c.match ? 'text-emerald-400' : 'text-red-400'}`}>
                      {c.match ? '✓' : ''} {c.variance >= 0 ? '+' : ''}{formatMoney(c.variance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {balance.counterparties.length > 10 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full py-1.5 text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
              >
                {expanded ? 'Show less' : `Show all ${balance.counterparties.length} ${counterpartyLabel.toLowerCase()}s`}
              </button>
            )}
          </div>
        )}

        {/* Missing entries */}
        {balance.missing_in_ours && balance.missing_in_ours.length > 0 && (
          <div className="mt-2 text-[10px] text-amber-400">
            Missing in FinBoard: {balance.missing_in_ours.map(m => m.name).join(', ')}
          </div>
        )}
        {balance.missing_in_qb && balance.missing_in_qb.length > 0 && (
          <div className="mt-1 text-[10px] text-sky-400">
            Extra in FinBoard: {balance.missing_in_qb.map(m => m.name).join(', ')}
          </div>
        )}
      </div>
    </div>
  );
}
